import {
  IDataTableColumn,
  useGridState,
  useSearchApi,
  ButtonContainer,
  FieldSet,
  showSuccess,
  showError,
} from "nsitools-react";
import * as React from "react";

import { BasePersonSearch, MembershipHIstoryApi, PersonMembershipHistoryGridDto } from "../../../../../api";
import { CustomDataTable, DeleteButton, EditButton, ExportButton } from "../../../../../components";
import { useGlobalData, useDialog, useAuth } from "../../../../../contexts";
import { useApiService, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { useParams } from "react-router";
import { Dialog, Classes, Colors, Intent, Checkbox, Button, ButtonGroup } from "@blueprintjs/core";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { ERoutes } from "../../../../../ERoutes";
import { PersonMembershipHistoryPanel } from "./PersonMemberShipHistoryPanel";

export interface IPersonMembershipHistoryListProps {
  personId: number;
}

const Container = styled.div`
  flex: 1;
  height: 100%;
  & > .${Classes.PANEL_STACK} {
    height: 100%;
  }
`;

export const PersonMembershipHistoryList: React.FunctionComponent<IPersonMembershipHistoryListProps> = ({
  personId,
}) => {
  const { state } = useParams<{ state: string }>();
  const { showDialog } = useDialog();
  const { userPermissions } = useAuth();
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const api = useApiService(MembershipHIstoryApi);
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const editMode = React.useMemo(() => state === "edit", [state]);
  const [renewalLoading, setrenewalLoading] = React.useState<boolean>();
  const [sObjState, setsobjState] = React.useState<any>();
  // TODO CRR : Use Memo?
  const [authorizedEndOfRenewal] = React.useState(
    userPermissions.some(
      (c) => c.module?.toUpperCase() === "PEOPLE" && c.tab?.toUpperCase() === "ENDOFRENEWAL" && c.permission === "E"
    )
  );
  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { dateFrom: "DESC" },
  });

  const searchFunction = React.useCallback(
    (sObj: BasePersonSearch) => {
      const sobj = BasePersonSearch.fromJS({
        ...sObj,
        personId: personId,
        languageId: currentLanguage.languageId,
      });
      setsobjState(sobj);
      return api.baseSearch(sobj);
    },
    [api, currentLanguage.languageId, personId]
  );

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const endRenewal = React.useCallback(
    (personMembershipHistoryId: number) => {
      showDialog({
        message: t(ETLCodes.EndRenewalConfirmationMessage),
        title: t(ETLCodes.EndRenewalConfirmationTitle),
        onConfirmed: async () => {
          setrenewalLoading(true);
          try {
            const res = await api.disableRenewal(personMembershipHistoryId);
            search();
            setrenewalLoading(false);
            showSuccess(t(ETLCodes.RenewalSuccessfullyDisabled));
            return res;
          } catch {
            showError(t(ETLCodes.Error));
            setrenewalLoading(false);
          }
        },
      });
    },
    [api, search, showDialog, t]
  );

  const deleteItem = React.useCallback(
    async (membershipHistoryId) => {
      showDialog({
        message: t(ETLCodes.DeleteConfirmationMessage),
        title: t(ETLCodes.DeleteDialogTitle),
        onConfirmed: async () => {
          try {
            const res = await api.deleteMembershipHistory(membershipHistoryId);
            search();
            showSuccess(t(ETLCodes.DeleteSuccess));
            return res;
          } catch {
            showError(t(ETLCodes.Error));
          }
        },
      });
    },
    [api, search, showDialog, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        hidden: !editMode,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: PersonMembershipHistoryGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.membershipHistoryId)} />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.MembershipType),
        fieldName: "membershipType",
      },
      {
        header: () => t(ETLCodes.DateFrom),
        fieldName: "dateFrom",
      },
      {
        header: () => t(ETLCodes.DateTo),
        fieldName: "dateTo",
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
        render: (row: PersonMembershipHistoryGridDto) => (
          <Link target="_blank" to={`${ERoutes.membershipRequest}/${row.membershipRequestId}/detail/view`}>
            {row.requestNumber}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.EndOfRenewal),
        fieldName: "endOfRenewalAsked",
        computed: true,
        disableSorting: true,
        render: (row: PersonMembershipHistoryGridDto) =>
          row.dateFrom <= new Date() && row.dateTo >= new Date() && !row.endOfRenewalAsked && authorizedEndOfRenewal ? (
            <Button
              text={t(ETLCodes.EndOfRenewal)}
              onClick={() => endRenewal(row.membershipHistoryId)}
              loading={renewalLoading}
            />
          ) : (
            <Checkbox checked={row.endOfRenewalAsked} readOnly disabled />
          ),
      },
    ],
    [authorizedEndOfRenewal, deleteItem, editMode, endRenewal, renewalLoading, t]
  );

  const { totalCount } = tableState;

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.MembershipHistory, { count: totalCount })}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
            {editMode && (
              <EditButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction();
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.MembershipHistory)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ background: Colors.WHITE }}
      >
        <Container className={Classes.DIALOG_BODY}>
          <PersonMembershipHistoryPanel
            personId={personId}
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
          />
        </Container>
      </Dialog>
    </>
  );
};
